import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { paymentService } from '../../../../services/paymentService';
import { installmentPaymentService } from '../../../../services/installmentPaymentService';
import { validateField } from '../../../../services/inputCheck';
import PaymentPlanSelector from '../PaymentPlanSelector/PaymentPlanSelector';
import ToastNotification from '../../../../components/ToastNotification/ToastNotification';
import './PaymentForm.scss';
import SvgIcons from '../../../../services/SvgIcons';
import { checkAccessService } from '../../../../services/api/checkAccessService';
import { useAuth } from '../../../../contexts/AuthContext';

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(STRIPE_KEY);

// Apparence Stripe cohérente avec la charte graphique
const stripeAppearance = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#6D4F47',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#e74c3c',
        fontFamily: 'Inter, system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px'
    }
};

const applyPromoDiscount = (amount, promoData) => {
    if (!promoData) return amount;
    if (promoData.type === 'pourcentage') {
        return Math.max(0.5, Math.round(amount * (1 - promoData.valeur / 100) * 100) / 100);
    }
    return Math.max(0.5, Math.round((amount - promoData.valeur) * 100) / 100);
};

// Composant principal
const PaymentForm = ({ productId, workshop_infos, onCancel, training }) => {
    const { user } = useAuth();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [userInfo, setUserInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        zipcode: ''
    });
    const [promoCode, setPromoCode] = useState('');
    const [promoData, setPromoData] = useState(null);
    const [promoError, setPromoError] = useState('');
    const [promoLoading, setPromoLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setUserInfo(prev => ({
                firstName: user.firstname || prev.firstName,
                lastName: user.lastname || prev.lastName,
                email: user.email || prev.email,
                phone: user.telephone || prev.phone,
                zipcode: user.codePostal || prev.zipcode
            }));
        }
    }, [user]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [notification, setNotification] = useState(null);
    const [accessCheck, setAccessCheck] = useState({
        isChecking: false,
        hasAccess: false,
        message: ''
    });

    // Utiliser les vraies données du training
    const trainingData = useMemo(() => {
        if (training?.PageDePaiement) return training;
        return {
            PageDePaiement: {
                TarifGlobal: 97,
                Mensualites: [{ TarifParMois: 49, NbMensualite: 2 }]
            }
        };
    }, [training]);

    // L'ID produit réel = slug de la formation
    const realProductId = training?.slug || productId;

    // Sélection du plan — réinitialise le code promo car les montants changent
    const handlePlanSelect = useCallback((plan) => {
        const planData = plan.data || plan;
        setSelectedPlan({
            type: plan.type,
            installments: Number(planData.installments) || 1,
            amount: Number(planData.amount),
            totalAmount: Number(planData.totalAmount) || (Number(planData.amount) * (Number(planData.installments) || 1)),
            description: planData.description || 'Paiement'
        });
        setPromoData(null);
        setPromoCode('');
        setPromoError('');
    }, []);

    const handleApplyPromo = useCallback(async () => {
        if (!promoCode.trim()) return;
        if (!workshop_infos?.id) return;
        setPromoLoading(true);
        setPromoError('');
        setPromoData(null);
        const result = await paymentService.validatePromo(promoCode.trim(), workshop_infos.id);
        setPromoLoading(false);
        if (result.valid) {
            setPromoData(result);
        } else {
            setPromoError(result.message || 'Code invalide');
        }
    }, [promoCode, workshop_infos]);

    // Plan effectif avec remise appliquée si code promo valide
    const effectivePlan = useMemo(() => {
        if (!selectedPlan) return null;
        if (!promoData) return selectedPlan;
        const discountedAmount = applyPromoDiscount(selectedPlan.amount, promoData);
        const discountedTotal = applyPromoDiscount(selectedPlan.totalAmount, promoData);
        return { ...selectedPlan, amount: discountedAmount, totalAmount: discountedTotal };
    }, [selectedPlan, promoData]);

    // Options Elements en mode différé (pas besoin de clientSecret au départ)
    const elementsOptions = useMemo(() => {
        if (!effectivePlan) return null;
        const isInstallment = effectivePlan.installments > 1;
        return {
            mode: 'payment',
            amount: Math.round(effectivePlan.amount * 100),
            currency: 'eur',
            appearance: stripeAppearance,
            // Paiement en plusieurs fois : carte uniquement (PayPal ne supporte pas l'off-session)
            // Paiement unique : carte + PayPal
            paymentMethodTypes: isInstallment ? ['card'] : ['card', 'paypal'],
            // Pour les paiements échelonnés, on sauvegarde la carte
            ...(isInstallment && { setup_future_usage: 'off_session' })
        };
    }, [effectivePlan]);

    const handleUserInfoChange = useCallback((e) => {
        const { name, value } = e.target;
        setUserInfo(prev => ({ ...prev, [name]: value }));
        if (hasAttemptedSubmit) {
            const fieldName = name === 'firstName' ? 'firstname' : name === 'lastName' ? 'lastname' : name;
            const error = validateField(fieldName, value);
            setFieldErrors(prev => ({ ...prev, [name]: error }));
        }
    }, [hasAttemptedSubmit]);

    const validateForm = useCallback(() => {
        const errors = {};
        const checks = {
            firstName: validateField('firstname', userInfo.firstName),
            lastName: validateField('lastname', userInfo.lastName),
            email: validateField('email', userInfo.email),
            phone: validateField('phone', userInfo.phone),
            zipcode: validateField('zipcode', userInfo.zipcode)
        };
        Object.entries(checks).forEach(([key, err]) => { if (err) errors[key] = err; });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [userInfo]);

    const checkUserAccessBeforePayment = useCallback(async (email, pid) => {
        setAccessCheck(prev => ({ ...prev, isChecking: true }));
        try {
            const result = await checkAccessService.checkUserAccess(email, pid);
            setAccessCheck({ isChecking: false, hasAccess: result.hasAccess, message: result.message || '' });
            if (result.hasAccess) {
                setNotification({ message: result.message, type: 'warning' });
                return false;
            }
            return true;
        } catch (error) {
            console.error('Erreur vérification accès:', error);
            setAccessCheck(prev => ({ ...prev, isChecking: false }));
            return true;
        }
    }, []);

    const inputField = (name, placeholder, type = 'text', extra = {}) => (
        <div className={`payment-form__input-group ${fieldErrors[name] ? 'has-error' : ''}`}>
            <input
                type={type}
                name={name}
                value={userInfo[name]}
                onChange={handleUserInfoChange}
                placeholder={placeholder}
                required
                {...extra}
            />
            {fieldErrors[name] && (
                <div className="payment-form__field-error">
                    <SvgIcons name="warning" width="16" height="16" color="#e74c3c" />
                    {fieldErrors[name]}
                </div>
            )}
        </div>
    );

    return (
        <div className="payment-form">
            {notification && (
                <ToastNotification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}

            {accessCheck.hasAccess && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                    <h3>⚠️ {accessCheck.message}</h3>
                    <a href="/connexion">Se connecter à mon espace</a>
                </div>
            )}

            {/* 1. Sélection du plan */}
            <div className="payment-form__plan-selector">
                <PaymentPlanSelector
                    training={trainingData}
                    onPlanSelect={handlePlanSelect}
                    selectedPlan={selectedPlan}
                />
            </div>

            {/* 2. Formulaire toujours visible */}
            <div className="payment-form__user-and-payment-info">
                {/* Informations personnelles */}
                <div className="payment-form__user-info">
                    <h3>Tes informations</h3>
                    {inputField('firstName', 'Prénom')}
                    {inputField('lastName', 'Nom')}
                    {inputField('email', 'Email', 'email')}
                    {inputField('phone', 'Téléphone', 'tel')}
                    {inputField('zipcode', 'Code postal', 'text', { pattern: '[0-9]{5}', maxLength: '5' })}
                </div>

                {/* Code promo */}
                {selectedPlan && (
                    <div className="payment-form__promo">
                        <h3>Code de réduction</h3>
                        <div className="payment-form__promo-input-row">
                            <input
                                type="text"
                                value={promoCode}
                                onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); setPromoData(null); }}
                                placeholder="Entre ton code"
                                className="payment-form__promo-input"
                                disabled={promoLoading}
                                onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                            />
                            <button
                                type="button"
                                onClick={handleApplyPromo}
                                className="payment-form__promo-btn"
                                disabled={promoLoading || !promoCode.trim()}
                            >
                                {promoLoading ? '...' : 'Appliquer'}
                            </button>
                        </div>
                        {promoError && (
                            <p className="payment-form__promo-error">{promoError}</p>
                        )}
                        {promoData && (
                            <div className="payment-form__promo-success">
                                <span>✓ {promoData.message}</span>
                                <button type="button" className="payment-form__promo-remove" onClick={() => { setPromoData(null); setPromoCode(''); }}>
                                    Retirer
                                </button>
                            </div>
                        )}
                        {promoData && effectivePlan && (
                            <div className="payment-form__promo-recap">
                                <span className="payment-form__promo-original">{selectedPlan.amount}€</span>
                                <span className="payment-form__promo-final">{effectivePlan.amount}€</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Section Stripe */}
                <div className="payment-form__payment-info">
                    <h3>Tes informations de paiement</h3>

                    {effectivePlan && elementsOptions ? (
                        <Elements stripe={stripePromise} options={elementsOptions} key={`${selectedPlan.type}-${promoData?.promoCodeId ?? 'no-promo'}`}>
                            <StripeCheckoutSection
                                selectedPlan={effectivePlan}
                                userInfo={userInfo}
                                workshop_infos={workshop_infos}
                                training={training}
                                productId={realProductId}
                                promoCodeId={promoData?.promoCodeId ?? null}
                                onFormValidation={() => {
                                    setHasAttemptedSubmit(true);
                                    return validateForm();
                                }}
                                onError={(msg) => setNotification({ message: msg, type: 'error' })}
                                checkUserAccessBeforePayment={checkUserAccessBeforePayment}
                            />
                        </Elements>
                    ) : (
                        <div className="payment-form__placeholder">
                            <p>Sélectionne un mode de paiement pour continuer</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// Composant Stripe — s'occupe de la création du PI et de la confirmation
const StripeCheckoutSection = ({
    selectedPlan,
    userInfo,
    workshop_infos,
    training,
    productId,
    promoCodeId,
    onFormValidation,
    onError,
    checkUserAccessBeforePayment
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!stripe || !elements) return;

        // 1. Valider le formulaire utilisateur
        const isFormValid = onFormValidation && onFormValidation();
        if (!isFormValid) {
            onError && onError('Veuillez corriger les erreurs dans le formulaire.');
            return;
        }

        // 2. Vérifier que l'utilisateur n'a pas déjà accès
        const canProceed = await checkUserAccessBeforePayment(userInfo.email, productId);
        if (!canProceed) return;

        setIsProcessing(true);
        setError(null);

        try {
            // 3. Valider les éléments Stripe (requis en mode différé)
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setError(submitError.message);
                onError && onError(submitError.message);
                setIsProcessing(false);
                return;
            }

            // Métadonnées client complètes pour le webhook
            const customerMetadata = {
                customerEmail: userInfo.email,
                customerFirstName: userInfo.firstName,
                customerLastName: userInfo.lastName,
                customerPhone: userInfo.phone,
                customerZipcode: userInfo.zipcode,
                workshopTitle: workshop_infos?.title2 || workshop_infos?.title1 || '',
                workshopId: String(workshop_infos?.id || '')
            };

            let clientSecret, installmentPlanId;

            // 4. Créer le Payment Intent avec toutes les infos
            if (selectedPlan.installments === 1) {
                // Paiement unique
                const response = await paymentService.createPaymentIntent(
                    selectedPlan.amount,
                    productId,
                    customerMetadata,
                    promoCodeId
                );
                clientSecret = response.clientSecret;
            } else {
                // Paiement échelonné
                const response = await installmentPaymentService.createInstallmentPlan(
                    {
                        type: 'installment',
                        amount: selectedPlan.amount,
                        installments: selectedPlan.installments,
                        totalAmount: selectedPlan.totalAmount,
                        description: selectedPlan.description
                    },
                    {
                        productId,
                        firstName: userInfo.firstName,
                        lastName: userInfo.lastName,
                        email: userInfo.email,
                        phone: userInfo.phone,
                        metadata: customerMetadata,
                        promoCodeId
                    }
                );
                clientSecret = response.clientSecret;
                installmentPlanId = response.installmentPlanId;
            }

            // 5. Sauvegarder les infos pour la page de confirmation
            localStorage.setItem('payment_plan', JSON.stringify(selectedPlan));
            localStorage.setItem('user_info', JSON.stringify(userInfo));
            localStorage.setItem('workshop_info', JSON.stringify({ ...workshop_infos, slug: productId }));

            // 6. URL de retour après paiement
            let returnUrl = `${window.location.origin}/paiement/confirmation`;
            if (installmentPlanId) {
                returnUrl += `?installment_plan=${installmentPlanId}`;
            }

            // 7. Confirmer le paiement
            const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: returnUrl,
                    payment_method_data: {
                        billing_details: {
                            name: `${userInfo.firstName} ${userInfo.lastName}`.trim(),
                            email: userInfo.email
                        }
                    }
                },
                redirect: 'if_required'
            });

            if (stripeError) {
                setError(stripeError.message);
                onError && onError(`Erreur de paiement : ${stripeError.message}`);
            } else if (paymentIntent?.status === 'succeeded') {
                const confirmUrl = installmentPlanId
                    ? `${window.location.origin}/paiement/confirmation?payment_intent=${paymentIntent.id}&installment_plan=${installmentPlanId}&redirect_status=succeeded`
                    : `${window.location.origin}/paiement/confirmation?payment_intent=${paymentIntent.id}&redirect_status=succeeded`;
                window.location.href = confirmUrl;
            }
        } catch (err) {
            console.error('Erreur paiement:', err);
            setError(err.message);
            onError && onError(err.message || 'Une erreur inattendue est survenue');
        } finally {
            setIsProcessing(false);
        }
    }, [stripe, elements, userInfo, selectedPlan, workshop_infos, productId, promoCodeId, onFormValidation, onError, checkUserAccessBeforePayment]);

    if (!stripe || !elements) {
        return (
            <div className="payment-form__loading">
                <SvgIcons name="loading" width="24" height="24" color="#6D4F47" />
                <p>Chargement de Stripe...</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="payment-form__checkout">
            <div className="payment-form__element">
                <PaymentElement options={{ wallets: { link: 'never' } }} />
            </div>

            {selectedPlan.installments > 1 && (
                <div className="payment-form__installment-notice">
                    <p>🔒 Ta carte sera enregistrée de manière sécurisée pour les prélèvements futurs</p>
                    <p>📅 Tu recevras un email de rappel avant chaque prélèvement</p>
                </div>
            )}

            {error && (
                <div className="payment-form__error">❌ {error}</div>
            )}

            <button
                type="submit"
                className="payment-form__submit"
                disabled={isProcessing || !stripe}
            >
                {isProcessing ? (
                    <span className="payment-form__loading-text">
                        <SvgIcons name="loading" width="24" height="24" color="#FFFFFF" />
                        Traitement en cours...
                    </span>
                ) : (
                    selectedPlan.installments === 1
                        ? `Payer ${selectedPlan.amount}€`
                        : `Payer le 1er versement (${selectedPlan.amount}€)`
                )}
            </button>
        </form>
    );
};

export default PaymentForm;
